import { useState, useEffect, useCallback } from 'react';

import { api } from '@/services/api-class';

// ── Types ────────────────────────────────────────────────────────────

interface RegistrationErrors {
  username: string;
  password: string;
  confirmPassword: string;
  pin: string;
  confirmPin: string;
  terms: string;
  general: string;
}

const emptyErrors: RegistrationErrors = {
  username: '',
  password: '',
  confirmPassword: '',
  pin: '',
  confirmPin: '',
  terms: '',
  general: '',
};

// ── Component ────────────────────────────────────────────────────────

interface RegistrationScreenProps {
  code?: string;
}

export function RegistrationScreen({ code }: RegistrationScreenProps = {}) {
  // Registration code from prop or URL search params (legacy fallback)
  const [registrationCode] = useState<string | null>(() => {
    if (code) return code;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code');
  });

  // Form fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Validation
  const [errors, setErrors] = useState<RegistrationErrors>({ ...emptyErrors });
  const [usernameValid, setUsernameValid] = useState<boolean | null>(null);
  const [validatingUsername, setValidatingUsername] = useState(false);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Clear username validation when username changes
  useEffect(() => {
    setUsernameValid(null);
    setErrors((prev) => ({ ...prev, username: '' }));
  }, [username]);

  const clearErrors = useCallback(() => {
    setErrors({ ...emptyErrors });
  }, []);

  const validateUsername = useCallback(() => {
    if (!username.trim()) return;

    setValidatingUsername(true);
    setUsernameValid(null);
    setErrors((prev) => ({ ...prev, username: '' }));

    const body = { username };

    api.validateUserName(
      body,
      (result: any) => {
        setValidatingUsername(false);
        if (result && result.validationError) {
          setUsernameValid(false);
          setErrors((prev) => ({
            ...prev,
            username: 'Username is not available. Please choose another.',
          }));
        } else {
          setUsernameValid(true);
        }
      },
      () => {
        setValidatingUsername(false);
        setUsernameValid(false);
        setErrors((prev) => ({
          ...prev,
          username: 'Error validating username. Please try again.',
        }));
      }
    );
  }, [username]);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      clearErrors();
      let hasErrors = false;
      const newErrors = { ...emptyErrors };

      if (!username.trim()) {
        newErrors.username = 'Username is required.';
        hasErrors = true;
      }

      if (!password) {
        newErrors.password = 'Password is required.';
        hasErrors = true;
      }

      if (password && password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match.';
        hasErrors = true;
      }

      if (!pin) {
        newErrors.pin = 'PIN is required.';
        hasErrors = true;
      }

      if (pin && pin !== confirmPin) {
        newErrors.confirmPin = 'PINs do not match.';
        hasErrors = true;
      }

      if (!agreedToTerms) {
        newErrors.terms = 'You must agree to the Terms and Conditions.';
        hasErrors = true;
      }

      if (hasErrors) {
        setErrors(newErrors);
        return;
      }

      setSubmitting(true);

      const data = {
        registrationCode,
        username,
        newPassword1: password,
        newPassword2: confirmPassword,
        newPin1: pin,
        newPin2: confirmPin,
        readTerms: agreedToTerms,
      };

      api.registerUser(
        JSON.stringify(data),
        () => {
          setSubmitting(false);
          setShowSuccessModal(true);
        },
        () => {
          setSubmitting(false);
          setErrors((prev) => ({
            ...prev,
            general: 'Registration failed. Please try again.',
          }));
        }
      );
    },
    [username, password, confirmPassword, pin, confirmPin, agreedToTerms, registrationCode, clearErrors]
  );

  const redirectToLogin = useCallback(() => {
    setRedirecting(true);
    window.location.href = '/';
  }, []);

  return (
    <div className="kst-registration-page">
      {/* Background Panel (hidden on mobile via CSS) */}
      <div
        className="kst-registration-bg"
        style={{ backgroundImage: "url('images/bg-01.jpg')" }}
      >
        <span className="kst-browser-label">
          Supported browsers: Edge, Safari, Chrome
        </span>
      </div>

      {/* Form Panel */}
      <div className="kst-registration-form-panel">
        <form className="kst-registration-form" onSubmit={handleSubmit}>
          <h1 className="kst-registration-title">Orna Registration</h1>

          {/* General Error */}
          {errors.general && (
            <div className="kst-reg-error kst-reg-error--general">
              {errors.general}
            </div>
          )}

          {/* Username */}
          <div className="kst-reg-input-group">
            <label className="kst-reg-label">Username</label>
            <div className="kst-reg-input-with-btn">
              <input
                type="text"
                className={`kst-reg-input${
                  errors.username ? ' kst-reg-input--error' : ''
                }${usernameValid === true ? ' kst-reg-input--success' : ''}`}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Type username here..."
                title="Your User Name will be used to log into ORNA, please use something you can remember. It also must be unique, please use the validate button to check."
                autoComplete="username"
              />
              <button
                type="button"
                className="kst-validate-btn"
                disabled={validatingUsername || !username.trim()}
                onClick={validateUsername}
              >
                {validatingUsername ? 'Checking...' : 'Validate'}
              </button>
            </div>
            {usernameValid === true && (
              <div className="kst-reg-success-text">Username is available</div>
            )}
            {errors.username && (
              <div className="kst-reg-error">{errors.username}</div>
            )}
          </div>

          {/* Password */}
          <div className="kst-reg-input-group">
            <label className="kst-reg-label">Password</label>
            <input
              type="password"
              className={`kst-reg-input${
                errors.password ? ' kst-reg-input--error' : ''
              }`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="*************"
              title="Passwords must be 8-50 characters with an uppercase letter, lowercase letter and one number or special character."
              autoComplete="new-password"
            />
            {errors.password && (
              <div className="kst-reg-error">{errors.password}</div>
            )}
          </div>

          {/* Repeat Password */}
          <div className="kst-reg-input-group">
            <label className="kst-reg-label">Repeat Password</label>
            <input
              type="password"
              className={`kst-reg-input${
                errors.confirmPassword ? ' kst-reg-input--error' : ''
              }`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="*************"
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <div className="kst-reg-error">{errors.confirmPassword}</div>
            )}
          </div>

          {/* PIN */}
          <div className="kst-reg-input-group">
            <label className="kst-reg-label">PIN</label>
            <input
              type="password"
              className={`kst-reg-input${
                errors.pin ? ' kst-reg-input--error' : ''
              }`}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Type PIN here..."
              title="This security pin number is 4-digits and is used by our company to verify your identity when you call. Please ensure it is memorable, but we suggest not using the same as used for personal finances."
            />
            {errors.pin && (
              <div className="kst-reg-error">{errors.pin}</div>
            )}
          </div>

          {/* Confirm PIN */}
          <div className="kst-reg-input-group">
            <label className="kst-reg-label">Confirm PIN</label>
            <input
              type="password"
              className={`kst-reg-input${
                errors.confirmPin ? ' kst-reg-input--error' : ''
              }`}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              placeholder="Re-type PIN here..."
            />
            {errors.confirmPin && (
              <div className="kst-reg-error">{errors.confirmPin}</div>
            )}
          </div>

          {/* Terms Checkbox */}
          <div className="kst-reg-checkbox-group">
            <input
              type="checkbox"
              id="chkAgreement"
              className="kst-reg-checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            <label htmlFor="chkAgreement" className="kst-reg-checkbox-label">
              I agree to the{' '}
              <a href="//kerkton.com/terms" target="_blank" rel="noreferrer">
                Terms of Conditions
              </a>{' '}
              &amp;{' '}
              <a href="//kerkton.com/privacy" target="_blank" rel="noreferrer">
                Privacy
              </a>
            </label>
            {errors.terms && (
              <div className="kst-reg-error">{errors.terms}</div>
            )}
          </div>

          {/* Submit Button */}
          <div className="kst-reg-submit-container">
            <button
              type="submit"
              className="kst-reg-submit-btn"
              disabled={submitting}
            >
              {submitting ? 'Signing Up...' : 'Sign Up'}
            </button>
          </div>
        </form>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="kst-reg-success-overlay" onClick={(e) => { if (e.target === e.currentTarget) redirectToLogin(); }}>
          <div className="kst-reg-success-modal">
            <h2 className="kst-reg-success-title">Registration Successful</h2>
            <p className="kst-reg-success-message">
              Please click the &quot;Sign In&quot; button below to go to the sign in screen.
            </p>
            <button
              type="button"
              className="kst-reg-success-btn"
              disabled={redirecting}
              onClick={redirectToLogin}
            >
              {redirecting ? 'Redirecting...' : 'Sign In'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegistrationScreen;
